import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl, MenuItem, Select, SelectChangeEvent,
} from '@mui/material';

export const SELECTOR_WIDTH: number = 240;
const NO_BUSINESS_UNITS_KEY = 'Informative.Negative.noBusinessUnits';

type BusinessUnit = {
  id: string;
  name: string;
};

interface BusinessUnitMultiSelectProps {
  canEditBusinessUnits: boolean;
  allBusinessUnits: BusinessUnit[];
  selectedBusinessUnits: string[];
  onChangeBusinessUnits: (businessUnits: string[]) => void;
}

export const BusinessUnitMultiSelect = ({
  canEditBusinessUnits,
  allBusinessUnits,
  selectedBusinessUnits,
  onChangeBusinessUnits,
}: BusinessUnitMultiSelectProps) => {
  const { t } = useTranslation();
  const noBusinessUnitsText = t(NO_BUSINESS_UNITS_KEY as any);

  const selectedBusinessUnitsIds = useMemo(
    () => (Array.isArray(selectedBusinessUnits) ? selectedBusinessUnits : []).map(String),
    [selectedBusinessUnits],
  );

  const allBusinessUnitsSafe = useMemo(
    () => (Array.isArray(allBusinessUnits) ? allBusinessUnits : []),
    [allBusinessUnits],
  );

  const businessUnitNameById = useMemo(
    () => allBusinessUnitsSafe.reduce((acc: Record<string, string>, businessUnit: BusinessUnit) => {
      acc[businessUnit.id] = businessUnit.name;
      return acc;
    }, {}),
    [allBusinessUnitsSafe],
  );

  const selectedBusinessUnitId = selectedBusinessUnitsIds[0] || '';

  const selectedLabels = useMemo(() => {
    if (!selectedBusinessUnitId) {
      return '';
    }

    return businessUnitNameById[selectedBusinessUnitId] || '';
  }, [businessUnitNameById, selectedBusinessUnitId]);

  const selectedLabelsOrFallback = selectedLabels || noBusinessUnitsText;

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    const nextSelectedBusinessUnits = value ? [value] : [];
    onChangeBusinessUnits(nextSelectedBusinessUnits);
  };

  if (!canEditBusinessUnits) {
    return selectedLabelsOrFallback;
  }

  return (
    <div>
      <FormControl sx={{ width: SELECTOR_WIDTH }} size="small">
        <Select
          onChange={handleChange}
          value={selectedBusinessUnitId}
          renderValue={() => selectedLabelsOrFallback}
          displayEmpty
        >
          <MenuItem value="">{noBusinessUnitsText}</MenuItem>
          {allBusinessUnitsSafe.map((businessUnit: BusinessUnit) => (
            <MenuItem key={businessUnit.id} value={businessUnit.id}>
              {businessUnit.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};